import "@shoelace-style/shoelace/dist/components/menu-item/menu-item.js";
import "@shoelace-style/shoelace/dist/components/select/select.js";
import "@shoelace-style/shoelace/dist/components/option/option.js";
import "@shoelace-style/shoelace/dist/components/textarea/textarea.js";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/progress-bar/progress-bar.js";
import "@shoelace-style/shoelace/dist/components/card/card.js";
import "@shoelace-style/shoelace/dist/components/tab/tab.js";
import "@shoelace-style/shoelace/dist/components/tab-panel/tab-panel.js";
import "@shoelace-style/shoelace/dist/components/icon-button/icon-button.js";
import "@shoelace-style/shoelace/dist/components/tab-group/tab-group.js";

import { v4 as uuidv4 } from "uuid";
import clone from "lodash-es/clone.js";
import uniq from "lodash-es/uniq.js";
import { until } from "lit/directives/until.js";
import { html, PropertyValueMap, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import {
    IAmenities,
    IDiveData,
    IMedia,
    IPlace,
    IPlaceData,
    IPlaceMediaItem,
    IReference,
    ISpelioData,
    IWreckData,
    makeDefaultDiveData,
    makeDefaultWreckData,
    makeEmptyAmenities,
} from "../../models/Models";
import { makeCustomEvent } from "../../Utils";
import { ILocation } from "../../models/ILocation";
import {
    CaveRating,
    ExplorationPotential,
    LocationType,
    TourismPotential,
    DisabledAccessPotential,
    WaterType,
    Difficulty,
    EntryType,
} from "../../../models";
import { TriState } from "../../../models";
import { uploadMediaEventHandler } from "../../Media";
import { litIf } from "../LitUtils";

import { MediaCarousel } from "../Media/MediaCarousel";
import SlButton from "@shoelace-style/shoelace/dist/components/button/button.js";
import { choose } from "lit/directives/choose.js";
import { groupAccessToProtectionLevel, ProtectionLevel, protectionLevelToGroups } from "../../User";
import { BaseElement } from "../BaseElement";
import { renderInput } from "../form/Input";
import { renderTriState } from "../form/TriState";
import { renderBoolean } from "../form/Boolean";
import { renderEnumSelect } from "../form/EnumSelect";
import { getModuleLog } from "../../Logger";
import { styles } from "./PlaceEditor.style";
import { IWarning, WarningTable } from "../../backend/cloud/WarningsTable";
import EasyMDE from "easymde";
import Fontawesome from "lit-fontawesome";
import { LightTheme } from "../ThemeLight";
import { Rating } from "../../../models";
import { getLocationTypeIsWet } from "../../PlaceUtils";
import { EasyMDEStyle } from "../Style/EasyMDE";
import { watch } from "../internal/watch";
import { PlaceTable } from "../../backend/cloud/PlaceTable";
import { userCanEditPlaceMedia } from "../../backend/IPlaceMediaTable";

import "../Media/Media";
import "../Media/MediaCarousel";
import "../TextToDecimalDegrees";
import "../DMSToDecimalDegrees";
import "../YouTubeEmbedControl/YouTubeEmbedControl";
import { Kernel } from "../../Kernel";
import { cloneDeep } from "lodash-es";

export enum EditorPlaceMediaInfoOp {
    Unchanged = "Unchanged",
    Added = "Added",
    Modified = "Modified",
    Deleted = "Deleted",
}

export type EditorPlaceMediaInfo = {
    info: IPlaceMediaItem;
    op: EditorPlaceMediaInfoOp;
};

@customElement("ds-place-editor")
export class PlaceEditor extends BaseElement {
    static override styles = [EasyMDEStyle, LightTheme, Fontawesome, styles];

    @property({ type: Object }) place?: IPlace;
    @property({ type: Boolean }) showDelete = false;
    @property({ type: Boolean }) noMediaGallery = false;
    @property({ type: Boolean }) noCommandBar: any;

    @state() protected _placeId = "";
    @state() protected _placeType: LocationType = LocationType.UNKNOWN;
    @state() protected _placeSource = "";
    @state() protected _placeLocationLat = 0;
    @state() protected _placeLocationLon = 0;
    @state() protected _placeName = "";
    @state() protected _placeSystemName = "";
    @state() protected _placeReference = "";
    @state() protected _placeNotes = "";
    @state() protected _placeTourismPotential: TourismPotential = TourismPotential.UNKNOWN;
    @state() protected _placeAccessDetails = "";
    @state() protected _placeVerticalAccess: TriState = TriState.UNKNOWN;
    @state() protected _placeCaveRating: CaveRating = CaveRating.UNKNOWN;
    @state() protected _placeCaveRatingHighest: CaveRating = CaveRating.UNKNOWN;
    @state() protected _placeExplorationPotential: ExplorationPotential =
        ExplorationPotential.UNKNOWN;
    @state() protected _placeDisabledAccessPotential: DisabledAccessPotential =
        DisabledAccessPotential.UNKNOWN;

    @state() protected _placeFiles: string[] = [];
    @state() protected _placeAlternateNames = "";
    @state() protected _placeLabels = "";
    @state() protected _placeReferences: Array<IReference> = [];
    @state() protected _placedCheckedBy = "";
    @state() protected _placeGoesToWater: TriState = TriState.UNKNOWN;
    @state() protected _placeLocationKnown = "No";
    @state() protected _placeChecked = "No";
    @state() protected _placeWarnings: string[] = [];
    @state() protected _surveyedLength?: number;

    @state() protected _diveData: IDiveData = makeDefaultDiveData();
    @state() protected _wreckData: IWreckData = makeDefaultWreckData();
    @state() protected _amenities: IAmenities = makeEmptyAmenities();
    @state() protected _uploadingMedia = false;
    @state() protected _uploadProgress = 0;
    @state() protected _uploadFilename = "";
    @state() protected _mediaImportError?: string;
    @state() protected _userAccess: string[] = [];
    @state() protected _protectionLevel?: ProtectionLevel;
    @state() protected _collectionCave = "No";
    @state() protected _collectionScuba = "No";

    @state() protected _placeMedia: EditorPlaceMediaInfo[] = [];

    private _markdownEditor?: EasyMDE;

    private _placeTable = this._kernel.dataStore.getSource<PlaceTable>("places");
    private _warningTable = this._kernel.dataStore.getSource<WarningTable>("warnings");
    private _log = getModuleLog("place-editor");

    public override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        this.addEventListener("ds-item-changed", () => {
            this._raiseModifiedEvent();
        });
    }

    protected override firstUpdated(
        _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>
    ): void {
        const el = this.shadowRoot?.getElementById("description-editor");
        if (el) {
            this._markdownEditor = new EasyMDE({ element: el, autoRefresh: { delay: 300 } });
            this._markdownEditor.codemirror.on("change", () => {
                if (this._markdownEditor) {
                    const newVal = this._markdownEditor.value();
                    if (this._placeNotes !== newVal) {
                        this._placeNotes = this._markdownEditor.value();
                        this._raiseModifiedEvent();
                    }
                }
            });
        }
    }

    @watch("place")
    async onPlaceUpdated() {
        // Center map and update marker whenever the lat / long properties change
        if (this.place) {
            this._placeId = this.place.id;
            this._placeType =
                (this.place.data.spelioData.type as LocationType) ?? LocationType.UNKNOWN;
            this._placeSource = this.place.data.spelioData.source;
            this._placeLocationLat = this.place.data.location.lat ?? 0;
            this._placeLocationLon = this.place.data.location.lon ?? 0;
            this._placeName = this.place.data.name;
            this._placeSystemName = this.place.data.spelioData.systemName ?? "";
            this._placeReference = this.place.data.spelioData.reference;
            this._placeNotes = this.place.data.spelioData.notes;
            this._placeTourismPotential = this.place.data.spelioData
                .tourismPotential as TourismPotential;
            this._placeAccessDetails = this.place.data.spelioData.accessDetails;
            this._placeVerticalAccess = this.place.data.spelioData.verticalAccess as TriState;
            this._placeCaveRating = this.place.data.spelioData.caveRating as CaveRating;
            this._placeCaveRatingHighest = this.place.data.spelioData
                .highestCaveRating as CaveRating;
            this._placeExplorationPotential = this.place.data.spelioData
                .explorationPotential as ExplorationPotential;
            this._placeDisabledAccessPotential = this.place.data.spelioData
                .disabledAccessPotential as DisabledAccessPotential;
            this._placeFiles = this.place.data.spelioData.files;
            this._placeAlternateNames = this.place.data.alternateNames.join(", ");
            this._placeLabels = this.place.data.spelioData.labels.join(", ");
            this._placeReferences = this.place.data.spelioData.references.map((x) => {
                return { name: x.name, text: x.text };
            });
            this._placedCheckedBy = this.place.data.checkedBy;
            this._placeGoesToWater = this.place.data.spelioData.goesToWater as TriState;
            this._placeLocationKnown = this.place.data.locationKnown ? "Yes" : "No";
            this._placeChecked = this.place.data.checked ? "Yes" : "No";
            this._placeMedia = (await this._placeTable.getMediaItems(this.place.id)).map((x) => {
                return {
                    op: EditorPlaceMediaInfoOp.Unchanged,
                    info: x,
                } as EditorPlaceMediaInfo;
            });
            this._placeWarnings = this.place.data.warnings ?? [];
            this._diveData = this.place.data.diveData ?? makeDefaultDiveData();
            this._wreckData = this.place.data.wreckData ?? makeDefaultWreckData();
            this._amenities = this.place.data.amenities ?? makeEmptyAmenities();
            this._protectionLevel =
                groupAccessToProtectionLevel(this.place.groupAccess) ?? ProtectionLevel.public;
            this._userAccess = this.place.userAccess ?? [];
            this._markdownEditor?.value(this._placeNotes);
            this._surveyedLength = this.place.data.spelioData.surveyedLength ?? undefined;
            this._collectionCave =
                this.place.caveCollection && this.place.caveCollection === "true" ? "Yes" : "No";
            this._collectionScuba =
                this.place.scubaCollection && this.place.scubaCollection === "true" ? "Yes" : "No";
        }
    }

    private _raiseModifiedEvent() {
        this.dispatchEvent(
            makeCustomEvent("on-modified", {
                place: this.toPlace(),
                media: cloneDeep(this._placeMedia),
            })
        );
    }

    public setPlaceLocation(location: ILocation) {
        if (this.place) {
            const p = clone(this.place);
            p.data.location = location;
            this.place = p;
            this._raiseModifiedEvent();
        }
    }

    private _makeUniqueStringList(str: string): string[] {
        return uniq(
            str
                .split(",")
                .map((x: string) => x.trim())
                .filter((x: string) => x)
        );
    }
    private _setLocationFromDMS() {
        this._currentDialog = "location-from-dms";
    }

    private _setLocationFromString() {
        this._currentDialog = "location-from-text";
    }

    private _openYouTubeEmbedDialog() {
        this._currentDialog = "youtube-embed";
    }

    private async _addYouTubeLink(url: string) {
        if (url && this._kernel.user?.id && this.place) {
            const placeMedia = {
                op: EditorPlaceMediaInfoOp.Added,
                info: {
                    id: uuidv4(),
                    owner: this._kernel.user.id,
                    groupAccess: this.place.groupAccess as string[],
                    media: {
                        id: uuidv4(),
                        mimeType: "application/youtube",
                        file: {
                            key: url,
                            bucket: "",
                            region: "",
                        },
                        dateAdded: new Date().toISOString(),
                    } as IMedia,
                } as IPlaceMediaItem,
            };
            this._placeMedia = [placeMedia, ...this._placeMedia];
            this._raiseModifiedEvent();
        }
    }

    private toPlace() {
        if (!this.place) {
            return undefined;
        }

        return {
            id: this.place.id,
            softDeleted: "false",
            groupAccess: this._protectionLevel
                ? protectionLevelToGroups(this._protectionLevel)
                : protectionLevelToGroups(ProtectionLevel.public),
            userAccess: this._userAccess,
            caveCollection: this._collectionCave === "Yes" ? "true" : "false",
            scubaCollection: this._collectionScuba === "Yes" ? "true" : "false",
            data: {
                name: this._placeName,
                address: this.place.data.address,
                altitude: this.place.data.altitude,
                checkedBy: this._placedCheckedBy,
                locationKnown: this._placeLocationKnown === "Yes" ? true : false,
                checked: this._placeChecked === "Yes" ? true : false,
                alternateNames: this._makeUniqueStringList(this._placeAlternateNames),
                location: {
                    lat: this._placeLocationLat,
                    lon: this._placeLocationLon,
                },
                spelioData: {
                    type: this._placeType,
                    source: this._placeSource,
                    reference: this._placeReference,
                    notes: this._placeNotes,
                    tourismPotential: this._placeTourismPotential,
                    accessDetails: this._placeAccessDetails,
                    verticalAccess: this._placeVerticalAccess,
                    caveRating: this._placeCaveRating,
                    highestCaveRating: this._placeCaveRatingHighest,
                    explorationPotential: this._placeExplorationPotential,
                    disabledAccessPotential: this._placeDisabledAccessPotential,
                    files: this._placeFiles,
                    labels: this._makeUniqueStringList(this._placeLabels),
                    references: this._placeReferences,
                    goesToWater: this._placeGoesToWater,
                    systemName: this._placeSystemName,
                    surveyedLength: this._surveyedLength,
                } as ISpelioData,
                warnings: this._placeWarnings,
                diveData: {
                    minDepth: this._diveData.minDepth,
                    maxDepth: this._diveData.maxDepth,
                    waterType: this._diveData.waterType,
                    isTidal: this._diveData.isTidal,
                    minTemp: this._diveData.minTemp,
                    maxTemp: this._diveData.maxTemp,
                    difficulty: this._diveData.difficulty,
                    entryDifficulty: this._diveData.entryDifficulty,
                    entryType: this._diveData.entryType,
                },
                wreckData: {
                    type: this._wreckData.type,
                    built: this._wreckData.built,
                    dateSunk: this._wreckData.dateSunk,
                    length: this._wreckData.length,
                    width: this._wreckData.width,
                    draught: this._wreckData.draught,
                },
                amenities: {
                    hasShowers: this._amenities.hasShowers,
                    hasToilets: this._amenities.hasToilets,
                    hasPhone: this._amenities.hasPhone,
                    food: this._amenities.food,
                    family: this._amenities.family,
                    parking: this._amenities.parking,
                },
            } as IPlaceData,
        } as IPlace;
    }

    public accept() {
        if (!this.place) {
            return;
        }

        this.dispatchEvent(
            makeCustomEvent("on-accept", {
                place: this.toPlace(),
                media: cloneDeep(this._placeMedia),
            })
        );
    }

    public cancel() {
        this.dispatchEvent(makeCustomEvent("on-cancel"));
        this.reset();
    }

    public delete() {
        this.dispatchEvent(makeCustomEvent("on-delete"));
        this.reset();
    }

    public reset() {
        this.place = undefined;
        this._placeType = LocationType.UNKNOWN;
        this._placeSource = "";
        this._placeLocationLat = 0;
        this._placeLocationLon = 0;
        this._placeName = "";
        this._placeReference = "";
        this._placeNotes = "";
        this._placeTourismPotential = TourismPotential.UNKNOWN;
        this._placeAccessDetails = "";
        this._placeVerticalAccess = TriState.UNKNOWN;
        this._placeCaveRating = CaveRating.UNKNOWN;
        this._placeExplorationPotential = ExplorationPotential.UNKNOWN;
        this._placeDisabledAccessPotential = DisabledAccessPotential.UNKNOWN;
        this._placeFiles = [];
        this._placeAlternateNames = "";
        this._placeLabels = "";
        this._placeGoesToWater = TriState.UNKNOWN;
        this._placeReferences = [];
        this._placedCheckedBy = "";
        this._placeLocationKnown = "No";
        this._placeChecked = "No";
        this._placeMedia = [];
        this._userAccess = [];
        this._protectionLevel = undefined;
        this._surveyedLength = undefined;
        this._diveData = makeDefaultDiveData();
        this._wreckData = makeDefaultWreckData();
        this._collectionCave = "No";
        this._collectionScuba = "No";
        this._placeMedia = [];
    }

    private _showError(msg: string) {
        this._log.error(`Media import error : ${msg}`);
        this._mediaImportError = msg;
    }

    private _showFirstMediaItemTab() {
        const numVisible = this._placeMedia.filter(
            (x) => x.op !== EditorPlaceMediaInfoOp.Deleted
        ).length;
        if (numVisible > 0) {
            (this.shadowRoot?.getElementById("media-tab-group") as any)?.show(
                this._placeMedia[0].info.media.file.key
            );
        }
    }

    private async _deleteMedia(placeMedia: EditorPlaceMediaInfo) {
        placeMedia.op = EditorPlaceMediaInfoOp.Deleted;
        this._placeMedia = [...this._placeMedia];
        this._raiseModifiedEvent();
        this._showFirstMediaItemTab();
    }

    private async _importMedia(event: any) {
        const user = Kernel.instance().user?.id;
        if (!this.place || !user) {
            return;
        }

        try {
            this._uploadingMedia = true;
            this._mediaImportError = undefined;
            const result = await uploadMediaEventHandler(event);
            if (result.succeeded && result.media) {
                const placeMedia = {
                    id: uuidv4(),
                    owner: user,
                    groupAccess: this.place.groupAccess,
                    media: result.media,
                } as IPlaceMediaItem;

                this._placeMedia = [
                    {
                        op: EditorPlaceMediaInfoOp.Added,
                        info: placeMedia,
                    },
                    ...this._placeMedia,
                ];
                this._raiseModifiedEvent();
            } else if (result.error) {
                this._showError(result.error);
            }
        } catch (error: any) {
            this._showError(error);
        } finally {
            this._uploadingMedia = false;
            this._uploadProgress = 0;
        }
    }

    private _renderReference(ref: IReference) {
        return html`
            <div class="reference flex-column">
                <div class="reference-header">
                    ${renderInput(this, ref, "name", undefined)}
                    <sl-icon-button
                        name="trash3"
                        style="1.8em"
                        @click=${() => {
                            this._placeReferences = this._placeReferences.filter((x) => x != ref);
                            this._raiseModifiedEvent();
                        }}
                    ></sl-icon-button>
                </div>
                <sl-textarea
                    rows="12"
                    value=${ref.text}
                    @sl-input=${(e: any) => {
                        ref.text = e.target.value;
                        this._raiseModifiedEvent();
                    }}
                >
                </sl-textarea>
            </div>
        `;
    }

    private _renderReferencesSection() {
        return html`
            <div class="references-container">
                <sl-tab-group>
                    ${this._placeReferences.map(
                        (ref, index) =>
                            html`<sl-tab slot="nav" .panel=${`ds-panel-${index}`}
                                    >${ref.name}</sl-tab
                                >
                                <sl-tab-panel .name=${`ds-panel-${index}`}>
                                    ${this._renderReference(ref)}
                                </sl-tab-panel>`
                    )}
                </sl-tab-group>
                <sl-button
                    class="block-button"
                    size="small"
                    variant="primary"
                    @click=${() =>
                        (this._placeReferences = [
                            ...this._placeReferences,
                            { name: "Untitled", text: "" },
                        ])}
                    >Add Reference</sl-button
                >
            </div>
        `;
    }

    private _showMediaSlide(index: number) {
        (this.shadowRoot?.getElementById("media-carousel") as MediaCarousel)?.goToSlide(index);
    }

    private _renderMediaUploadSection() {
        return html`
            ${this._uploadingMedia
                ? html` <div style="display:flex; flex-direction:column">
                      <h4>
                          Uploading
                          <span style="font-style:italic">${this._uploadFilename}</span>
                      </h4>
                      <sl-progress-bar .value=${this._uploadProgress * 100}></sl-progress-bar>
                  </div>`
                : html`${litIf(
                          !this.noMediaGallery,
                          () =>
                              html`<div class="media-gallery">
                                  <ds-media-carousel
                                      id="media-carousel"
                                      autoplay
                                      .mediaList=${this._placeMedia.map((x) => {
                                          return {
                                              media: x.info.media,
                                              editable: userCanEditPlaceMedia(
                                                  this._kernel.user,
                                                  x.info
                                              ),
                                          };
                                      })}
                                  ></ds-media-carousel>
                              </div>`
                      )}
                      <div class="file-list">
                          ${litIf(
                              !this.noCommandBar,
                              () =>
                                  html`<div class="file-list-buttons flex-row">
                                      <sl-button
                                          size="small"
                                          class="block-button"
                                          @click=${() => {
                                              if (this.place?.id) {
                                                  this._raiseEvent("ds-move-place", {
                                                      id: this.place.id,
                                                  });
                                              }
                                          }}
                                          >Move place</sl-button
                                      >
                                      <sl-button
                                          size="small"
                                          class="block-button"
                                          @click=${() =>
                                              this.shadowRoot
                                                  ?.getElementById("getResourceFile")
                                                  ?.click()}
                                      >
                                          Upload media
                                      </sl-button>
                                      <!-- span class="info-text"
                                  >Photo, video, kml, geojson, gpx, svg or pdf</span
                                  -->
                                      <input
                                          type="file"
                                          id="getResourceFile"
                                          style="display:none"
                                          @change=${this._importMedia}
                                      />
                                      <sl-button
                                          size="small"
                                          class="block-button"
                                          @click=${() => this._openYouTubeEmbedDialog()}
                                      >
                                          Add YouTube
                                      </sl-button>
                                  </div>`
                          )}
                          ${litIf(
                              !!this._mediaImportError,
                              () =>
                                  html`<span class="media-import-error"
                                      >${this._mediaImportError}</span
                                  >`
                          )}
                      </div>`}
        `;
    }

    private async _mediaChanged(placeMedia: EditorPlaceMediaInfo) {
        if (placeMedia.op === EditorPlaceMediaInfoOp.Unchanged) {
            placeMedia.op = EditorPlaceMediaInfoOp.Modified;
        }
        this._raiseModifiedEvent();
    }

    private _renderMediaEditSection() {
        return html`<div>
            <sl-tab-group id="media-tab-group">
                ${this._placeMedia
                    .filter((x) => x.op !== EditorPlaceMediaInfoOp.Deleted)
                    .map(
                        (placeMedia, index) =>
                            html` <sl-tab slot="nav" .panel="${placeMedia.info.id}"
                                    >${!placeMedia.info.media.name ||
                                    placeMedia.info.media.name.length === 0
                                        ? "Unnamed"
                                        : placeMedia.info.media.name}</sl-tab
                                >
                                <sl-tab-panel .name=${placeMedia.info.id}>
                                    <sl-card @click=${() => this._showMediaSlide(index)}>
                                        <div slot="header" class="editor-media-header">
                                            <h3>${placeMedia.info.media.name}</h3>
                                            <sl-icon-button
                                                class="editor-grid-item"
                                                name="trash3"
                                                style="float:right"
                                                @click=${async () => {
                                                    await this._deleteMedia(placeMedia);
                                                }}
                                            ></sl-icon-button>
                                        </div>
                                        <ds-media-editor
                                            .placeMedia=${placeMedia.info}
                                            @ds-modified=${(e: CustomEvent) => {
                                                placeMedia.info = e.detail.media;
                                                this._mediaChanged(placeMedia);
                                            }}
                                        ></ds-media-editor>
                                    </sl-card>
                                </sl-tab-panel>`
                    )}
            </sl-tab-group>
        </div>`;
    }

    private _renderUserPermissionsSection() {
        return html` <div class="permissions-user flex-column">
            <div style="display:flex; width:100%; gap:0.5em">
                <sl-input
                    id="add-user-input"
                    placeholder="Enter user id"
                    pattern="[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}"
                    @sl-input=${(e: any) => {
                        const elem = this.shadowRoot?.getElementById("add-user-button") as SlButton;
                        if (elem) {
                            elem.disabled = !e.target.checkValidity();
                        }
                    }}
                ></sl-input>
                <sl-button
                    id="add-user-button"
                    disabled
                    @click=${() => {
                        const input = this.shadowRoot?.getElementById(
                            "add-user-input"
                        ) as HTMLInputElement;
                        if (input) {
                            this._userAccess = [...this._userAccess, input.value];
                            input.value = "";
                            this._raiseModifiedEvent();
                        }
                    }}
                    >Add</sl-button
                >
            </div>

            ${this._userAccess.map(
                (x) =>
                    html`<div class="permissions-user-entry">
                        <span>${x}</span>
                        <sl-icon-button
                            style="margin-left:auto"
                            name="trash3"
                            @click=${() => {
                                this._userAccess = this._userAccess.filter((c) => x !== c);
                                this._raiseModifiedEvent();
                            }}
                        >
                        </sl-icon-button>
                    </div>`
            )}
        </div>`;
    }
    private _renderPermissionsSection() {
        return html``;
        /*html`<div>
            <p>Permissions</p>
            <sl-tab-group id="media-tab-group">
                <sl-tab slot="nav" panel="groups" active>Group</sl-tab>
                <!-- sl-tab slot="nav" panel="users">Users</sl-tab -->
                <sl-tab-panel name="groups" active>
                    <div>
                        ${renderEnumSelect(
                            this,
                            ProtectionLevel,
                            this,
                            "_protectionLevel",
                            undefined,
                            undefined,
                            (x: ProtectionLevel) => this._kernel.user?.inGroup(x) ?? false
                        )}
                    </div>
                </sl-tab-panel>
                <!-- sl-tab-panel name="users"> ${this._renderUserPermissionsSection()} </sl-tab-panel -->
            </sl-tab-group>
        </div>`;*/
    }

    private async _getWarningTitle(id: string) {
        const w = await this._warningTable.read(id);
        return w?.title ?? "";
    }

    private async _renderWarningsSection(): Promise<TemplateResult> {
        const warnings = await this._warningTable.all();
        return html`<div>
            <p>Warnings</p>
            <sl-select
                @sl-change=${(e: any) => {
                    this._placeWarnings = [...this._placeWarnings, e.target.value];
                    this._raiseModifiedEvent();
                }}
            >
                ${warnings
                    .filter((x: IWarning) => !this._placeWarnings.includes(x.id))
                    .map(
                        (x: IWarning) => html` <sl-option value="${x.id}">${x.title}</sl-option> `
                    )}
            </sl-select>
            ${this._placeWarnings.map(
                (x) =>
                    html`<div class="warning">
                        <sl-icon-button
                            name="trash3"
                            @click=${() => {
                                this._placeWarnings = this._placeWarnings.filter((w) => w !== x);
                            }}
                        ></sl-icon-button>
                        <span>${until(this._getWarningTitle(x), html``)}</span>
                    </div>`
            )}
        </div>`;
    }

    private _renderSectionHeader(title: string) {
        return html`
            <span></span>
            <h3>${title}</h3>
            <span></span>
        `;
    }

    private _showDiveDetails(): boolean {
        return this._placeGoesToWater == TriState.YES || getLocationTypeIsWet(this._placeType);
    }

    private _renderAmenitiesSection() {
        return html` ${this._renderSectionHeader("Amenities")}
        ${renderTriState(
            this,
            this._amenities,
            "hasShowers",
            "Has showers",
            "Whether showers are available at the site"
        )}
        ${renderTriState(
            this,
            this._amenities,
            "hasToilets",
            "Has toilets",
            "Whether toilets are available at the site"
        )}
        ${renderTriState(
            this,
            this._amenities,
            "hasPhone",
            "Has phone",
            "Whether there is mobile phone coverage at the site"
        )}
        ${renderEnumSelect(
            this,
            Rating,
            this._amenities,
            "food",
            "Food availability",
            "Is food available near the site"
        )}
        ${renderEnumSelect(
            this,
            Rating,
            this._amenities,
            "parking",
            "Parking availability",
            "How easy is it normally to park during the day"
        )}
        ${renderEnumSelect(
            this,
            Rating,
            this._amenities,
            "family",
            "Family friendliness",
            "Is the other activities for the rest of family here"
        )}`;
    }

    private _renderDiveSection() {
        return html`${litIf(
            this._showDiveDetails(),
            () => html`
                ${this._renderSectionHeader("Dive details")}
                ${renderEnumSelect(
                    this,
                    CaveRating,
                    this,
                    "_placeCaveRating",
                    "Lowest dive rating",
                    "What is the minimum cave diving level do you need to be to dive this cave"
                )}
                ${renderEnumSelect(
                    this,
                    CaveRating,
                    this,
                    "_placeCaveRatingHighest",
                    "Highest dive rating",
                    "What is the highest level of diving at this cave"
                )}
                ${renderInput(
                    this,
                    this._diveData,
                    "minDepth",
                    "Min water depth(m)",
                    "number",
                    false,
                    undefined,
                    "Minimum water depth in meters"
                )}
                ${renderInput(
                    this,
                    this._diveData,
                    "maxDepth",
                    "Max water depth(m)",
                    "number",
                    false,
                    undefined,
                    "Maximum water depth in meters"
                )}
                ${renderEnumSelect(
                    this,
                    WaterType,
                    this._diveData,
                    "waterType",
                    "Water type",
                    "Fresh or sea water"
                )}
                ${renderTriState(
                    this,
                    this._diveData,
                    "isTidal",
                    "Is Tidal",
                    "Whether this place is affected by the tide"
                )}
                ${renderEnumSelect(
                    this,
                    EntryType,
                    this._diveData,
                    "entryType",
                    "Entry type",
                    "How to get in to the site"
                )}
                ${renderInput(
                    this,
                    this._diveData,
                    "minTemp",
                    "Min water temp(C)",
                    "number",
                    false,
                    undefined,
                    "Minimum water temperature in celsius"
                )}
                ${renderInput(
                    this,
                    this._diveData,
                    "maxTemp",
                    "Max water temp(C)",
                    "number",
                    false,
                    undefined,
                    "Maximum water temperature in celsius"
                )}
                ${renderEnumSelect(
                    this,
                    Difficulty,
                    this._diveData,
                    "difficulty",
                    "Dive difficulty",
                    "For its rating how hard of a dive this is"
                )}
                ${renderEnumSelect(
                    this,
                    Difficulty,
                    this._diveData,
                    "entryDifficulty",
                    "Entry difficulty",
                    "Hard hard is it to access the water"
                )}
            `
        )}`;
    }

    private _renderWreckSection() {
        return html` ${litIf(
            this._placeType === LocationType.WRECK,
            () =>
                html` ${this._renderSectionHeader("Wreck details")}
                ${renderInput(
                    this,
                    this._wreckData,
                    "type",
                    "Wreck type",
                    "text",
                    false,
                    undefined,
                    "Type of wreck"
                )}
                ${renderInput(
                    this,
                    this._wreckData,
                    "built",
                    "Construction",
                    "text",
                    false,
                    undefined,
                    "Where / when the vessel was build"
                )}
                ${renderInput(
                    this,
                    this._wreckData,
                    "dateSunk",
                    "Date sunk (YYYY-MM-DD)",
                    "text",
                    false,
                    undefined,
                    "When the wreck was sunk (YYYY-MM-DD)"
                )}
                ${renderInput(
                    this,
                    this._wreckData,
                    "length",
                    "Length (m)",
                    "number",
                    false,
                    undefined,
                    "Length of the ship in meters"
                )}
                ${renderInput(
                    this,
                    this._wreckData,
                    "width",
                    "Width (m)",
                    "number",
                    false,
                    undefined,
                    "Ship width in meters"
                )}
                ${renderInput(
                    this,
                    this._wreckData,
                    "draught",
                    "Draught (m)",
                    "number",
                    false,
                    undefined,
                    "Ships draught in meters"
                )}`
        )}`;
    }

    private _renderRatingsSection() {
        return html`${this._renderSectionHeader("Ratings")}
        ${renderEnumSelect(
            this,
            ExplorationPotential,
            this,
            "_placeExplorationPotential",
            "Exploration",
            "How much potential there is for further exploration"
        )}
        ${renderEnumSelect(
            this,
            TourismPotential,
            this,
            "_placeTourismPotential",
            "Tourism",
            "How interesting is this place to the general public"
        )}
        ${renderEnumSelect(
            this,
            DisabledAccessPotential,
            this,
            "_placeDisabledAccessPotential",
            "Disabled",
            "How accessible this place is for physically disabled people"
        )}`;
    }

    private _renderBasicSection() {
        return html`
            ${litIf(
                this._kernel.user?.isDeveloper(),
                () =>
                    html`${renderBoolean(
                        this,
                        this,
                        "_collectionCave",
                        "Cave collection",
                        "Should this site be shown on cavedb"
                    )}
                    ${renderBoolean(
                        this,
                        this,
                        "_collectionScuba",
                        "Scuba collection",
                        "Should this site be shown on bombimi"
                    )}`
            )}
            ${renderEnumSelect(
                this,
                ProtectionLevel,
                this,
                "_protectionLevel",
                "Privacy",
                "Who can see this place",
                (x: ProtectionLevel) => this._kernel.user?.inGroup(x) ?? false
            )}
            ${renderEnumSelect(
                this,
                LocationType,
                this,
                "_placeType",
                "Place type",
                "What kind of feature this is"
            )}
            ${renderInput(
                this,
                this,
                "_placeName",
                "Name",
                "text",
                false,
                undefined,
                "Primary name of the place"
            )}
            ${renderInput(
                this,
                this,
                "_placeSystemName",
                "System name",
                "text",
                false,
                undefined,
                "System this connects to if applicable"
            )}
            ${renderInput(
                this,
                this,
                "_placeAlternateNames",
                "Other names",
                "text",
                false,
                undefined,
                "Alternate names (comma separated)"
            )}
            ${renderInput(
                this,
                this,
                "_placeLabels",
                "Labels",
                "text",
                false,
                undefined,
                "Labels (comma separated)"
            )}
            ${renderInput(
                this,
                this,
                "_placeReference",
                "Reference",
                "text",
                false,
                undefined,
                "Reference for this place if it has one, i.e. L204"
            )}
            ${renderInput(
                this,
                this,
                "_placeSource",
                "Source",
                "text",
                false,
                undefined,
                "Source of the place. Maybe text, url etc."
            )}
            ${renderInput(
                this,
                this,
                "_placeAccessDetails",
                "Access",
                "text",
                false,
                undefined,
                "Which organization controls access to the site"
            )}
        `;
    }

    private _renderLocationSection() {
        return html`${this._renderSectionHeader("Location")}
            ${renderInput(
                this,
                this,
                "_placeLocationLat",
                "Latitude",
                "number",
                false,
                undefined,
                "Latitude in decimal degrees"
            )}
            ${renderInput(
                this,
                this,
                "_placeLocationLon",
                "Longitude",
                "number",
                false,
                undefined,
                "Longitude in decimal degrees"
            )}
            <div class="editor-label editor-grid-item"></div>
            <div class="editor-grid-item">
                <sl-button size="small" @click=${() => this._setLocationFromDMS()}
                    >From DMS</sl-button
                >
                <sl-button size="small" @click=${() => this._setLocationFromString()}
                    >From text</sl-button
                >
            </div>
            <div class="editor-label editor-grid-item"></div>
            ${renderBoolean(
                this,
                this,
                "_placeLocationKnown",
                "Location known",
                "Is this a concrete location or approximate"
            )}
            ${renderTriState(
                this,
                this,
                "_placeChecked",
                "Checked",
                "Has this place had its location verified"
            )}
            ${renderInput(
                this,
                this,
                "_placedCheckedBy",
                "Checked by",
                "text",
                false,
                undefined,
                "Who verified the place location"
            )}`;
    }

    private _renderPropertiesSection() {
        return html`${this._renderSectionHeader("Properties")}
        ${renderTriState(this, this, "_placeGoesToWater", "Goes to water", "Does this go to water")}
        ${renderTriState(
            this,
            this,
            "_placeVerticalAccess",
            "Vertical access",
            "Is vertical access equipment and training required to enter"
        )}
        ${renderInput(
            this,
            this,
            "_surveyedLength",
            "Surveyed length(m)",
            "number",
            false,
            undefined,
            "Total surveyed length in metres"
        )}`;
    }

    private _renderPropertyPanel() {
        return html`
            <div class="grid-3-column">
                ${this._renderBasicSection()} ${this._renderLocationSection()}
                ${this._renderPropertiesSection()} ${this._renderDiveSection()}
                ${this._renderWreckSection()} ${this._renderAmenitiesSection()}
                ${this._renderRatingsSection()}
            </div>
            ${until(this._renderWarningsSection(), html``)}
        `;
    }
    override render(): TemplateResult {
        return html` ${choose(this._currentDialog, [
                [
                    "location-from-text",
                    () =>
                        html`<ds-text-to-decimal-degrees-dialog
                            open
                            @on-approved=${(e: CustomEvent) => {
                                this._placeLocationLat = e.detail.location.lat;
                                this._placeLocationLon = e.detail.location.lon;
                                this._raiseModifiedEvent();
                            }}
                            @on-cancel=${(e: Event) => {
                                e.stopPropagation();
                            }}
                            @sl-after-hide=${(e: Event) => {
                                this._dialogAfterHide(e);
                            }}
                        >
                        </ds-text-to-decimal-degrees-dialog>`,
                ],
                [
                    "location-from-dms",
                    () =>
                        html`<ds-dms-to-decimal-degrees-dialog
                            open
                            @on-approved=${(e: CustomEvent) => {
                                this._placeLocationLat = e.detail.location.lat;
                                this._placeLocationLon = e.detail.location.lon;
                                this._raiseModifiedEvent();
                            }}
                            @on-cancel=${(e: Event) => {
                                e.stopPropagation();
                            }}
                            @sl-after-hide=${(e: Event) => {
                                this._dialogAfterHide(e);
                            }}
                        >
                        </ds-dms-to-decimal-degrees-dialog>`,
                ],
                [
                    "youtube-embed",
                    () =>
                        html`<ds-youtube-embed-dialog
                            @on-cancel=${(e: Event) => {
                                e.stopPropagation();
                            }}
                            @sl-after-hide=${(e: Event) => {
                                this._dialogAfterHide(e);
                            }}
                            @ds-on-accept=${(e: CustomEvent) => {
                                this._addYouTubeLink(e.detail.url);
                            }}
                        ></ds-youtube-embed-dialog>`,
                ],
            ])}
            <div class="flex-column">
                ${this._renderMediaUploadSection()}
                <sl-tab-group>
                    <sl-tab slot="nav" panel="properties">Properties</sl-tab>
                    <sl-tab slot="nav" panel="description">Description</sl-tab>
                    <sl-tab slot="nav" panel="media">Media</sl-tab>
                    <sl-tab slot="nav" panel="references">References</sl-tab>
                    <sl-tab-panel name="description">
                        <textarea id="description-editor"> </textarea>
                    </sl-tab-panel>
                    <sl-tab-panel name="properties" active
                        >${this._renderPropertyPanel()}</sl-tab-panel
                    >
                    <sl-tab-panel name="media"> ${this._renderMediaEditSection()}</sl-tab-panel>
                    <sl-tab-panel name="references"
                        >${this._renderReferencesSection()}</sl-tab-panel
                    >
                </sl-tab-group>
            </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-place-editor": PlaceEditor;
    }
}
